import { useCallback } from 'react';

import { PeriodPicker } from '@app/shared/components/PeriodPicker';

import { DATE_PRESETS, type DatePresetId } from '../../data/datePresets';

import type { SelectionPhase } from '../../hooks/useTaskComposer';
import type { Dayjs } from 'dayjs';
import type { ChangeEvent, FC } from 'react';

import './TaskDatePicker.scss';

interface TaskDatePickerProps {
  startDate: Dayjs;
  endDate: Dayjs;
  preset: DatePresetId | null;
  selectionPhase: SelectionPhase;
  previewStart: Dayjs | null;
  previewEnd: Dayjs | null;
  viewMonth: Dayjs;
  isAllDay: boolean;
  startTime: string;
  endTime: string;
  onPresetSelect(id: DatePresetId): void;
  onDayClick(day: Dayjs): void;
  onDayHover(day: Dayjs | null): void;
  onViewMonthChange(month: Dayjs): void;
  onToggleAllDay(): void;
  onStartTimeChange(time: string): void;
  onEndTimeChange(time: string): void;
  onSetDuration(minutes: number): void;
  onClose(): void;
}

const DURATION_SHORTCUTS: { label: string; minutes: number }[] = [
  { label: '+30м', minutes: 30 },
  { label: '+1ч', minutes: 60 },
  { label: '+2ч', minutes: 120 },
];

// Task date chip popup: the shared PeriodPicker with the task presets and the
// all-day / time column on the right. Day clicks write straight into the task
// form (nothing is sent until the task itself is saved); the popup stays open
// until «Сохранить» or an outside click.
export const TaskDatePicker: FC<TaskDatePickerProps> = ({
  startDate,
  endDate,
  preset,
  selectionPhase,
  previewStart,
  previewEnd,
  viewMonth,
  isAllDay,
  startTime,
  endTime,
  onPresetSelect,
  onDayClick,
  onDayHover,
  onViewMonthChange,
  onToggleAllDay,
  onStartTimeChange,
  onEndTimeChange,
  onSetDuration,
  onClose,
}) => {
  const handlePresetClick = useCallback(
    (id: DatePresetId) => {
      onPresetSelect(id);
      onClose();
    },
    [onPresetSelect, onClose]
  );

  const timeColumn = (
    <div className="TaskDatePicker">
      <label className="TaskDatePicker__allday">
        <span className="TaskDatePicker__allday-text">Весь день</span>
        <button
          type="button"
          role="switch"
          aria-checked={isAllDay}
          className={[
            'TaskDatePicker__toggle',
            isAllDay && 'TaskDatePicker__toggle--on',
          ]
            .filter(Boolean)
            .join(' ')}
          onClick={onToggleAllDay}
        />
      </label>

      {!isAllDay && (
        <div className="TaskDatePicker__time-fields">
          <div className="TaskDatePicker__time-row">
            <span className="TaskDatePicker__time-label">Начало</span>
            <input
              type="time"
              className="TaskDatePicker__time-input"
              value={startTime}
              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                onStartTimeChange(e.target.value)
              }
            />
          </div>

          <div className="TaskDatePicker__time-row">
            <span className="TaskDatePicker__time-label">Конец</span>
            <input
              type="time"
              className="TaskDatePicker__time-input"
              value={endTime}
              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                onEndTimeChange(e.target.value)
              }
            />
          </div>

          <div className="TaskDatePicker__duration">
            {DURATION_SHORTCUTS.map(({ label, minutes }) => (
              <button
                key={minutes}
                type="button"
                className="TaskDatePicker__duration-btn"
                onClick={() => onSetDuration(minutes)}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );

  return (
    <PeriodPicker
      presets={DATE_PRESETS}
      activePresetId={preset}
      viewMonth={viewMonth}
      startDate={startDate}
      endDate={endDate}
      isSelecting={selectionPhase === 'selecting'}
      previewStart={previewStart}
      previewEnd={previewEnd}
      aside={timeColumn}
      onPresetSelect={handlePresetClick}
      onDayClick={onDayClick}
      onDayHover={onDayHover}
      onViewMonthChange={onViewMonthChange}
      onSave={onClose}
    />
  );
};
